/**
 * Where the identity provider sends people back: /sso/complete and /sso/error.
 *
 * The code on /sso/complete is single-use, so, as with the emailed link, the
 * exchange is attempted exactly once however many times React mounts the
 * page.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { homeFor } from './home';
import { completeSsoSignIn, describeSsoError } from './sso';
import { isCallFailure } from '../lib/callable';
import { useAuth } from './useAuth';
import { Spinner } from '../app/Spinner';
import styles from './SignIn.module.css';
import { Wordmark } from '../shared/Wordmark';

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Wordmark withIcon className={styles.brandMark} />
        </div>
        <h1 className={styles.title}>{title}</h1>
        {children}
        <Link to="/signin" className={styles.submit} style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
          Back to sign in
        </Link>
      </div>
    </main>
  );
}

export function SsoCompletePage() {
  const [params] = useSearchParams();
  const code = params.get('code');
  const { session } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  // Derived, not stored: there is nothing to wait for when the code is missing.
  const missingCode = !code ? 'The sign-in came back without a code. Try again from the sign-in page.' : null;

  useEffect(() => {
    if (attempted.current || !code) return;
    attempted.current = true;
    completeSsoSignIn(code).catch((err: unknown) => {
      setError(isCallFailure(err) ? err.message : 'The sign-in could not be completed. Try again from the sign-in page.');
    });
  }, [code]);

  if (session) return <Navigate to={homeFor(session)} replace />;

  if (error ?? missingCode) {
    return (
      <Card title="Sign-in did not complete">
        <div className={styles.error} role="alert">{error ?? missingCode}</div>
      </Card>
    );
  }
  return <Spinner label="Signing you in" />;
}

export function SsoErrorPage() {
  const [params] = useSearchParams();
  const message = describeSsoError(params.get('error'), params.get('error_description'));
  return (
    <Card title="Sign-in did not complete">
      <div className={styles.error} role="alert">{message}</div>
    </Card>
  );
}
